/**
 * MobileAlertBanner
 *
 * Top-of-screen alert banner for the mobile guard-location redesign.
 * Displays the highest-priority undismissed alert with severity styling.
 * Features:
 * - Filters to undismissed alerts only
 * - Shows highest-severity alert (critical > warning)
 * - Severity-colored background and dot indicator
 * - Relative time display ("2m ago")
 * - "View" action button
 * - Collapsed mode shows a subtle info bar
 *
 * Visual Design (critical):
 * ┌─ [red dot] SOS — John Doe ──────── [View] ─┐
 * │            2m ago                            │
 * └──────────────────────────────────────────────┘
 */

import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DispatchAlert, AlertType } from './AlertsBar';
import { formatRelativeTime } from './utils';
import styles from './MobileAlertBanner.module.css';
import classNames from 'classnames';

// ============================================
// Types
// ============================================

interface MobileAlertBannerProps {
  alerts: DispatchAlert[];
  onView: (alert: DispatchAlert) => void;
  collapsed?: boolean;
}

// ============================================
// Constants
// ============================================

/** Severity sort order — lower number = higher priority */
const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  warning: 1,
};

/** Human-readable labels for alert types */
const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  no_activity: 'No Activity',
  left_site: 'Left Site',
  low_battery: 'Low Battery',
  incomplete_patrol: 'Incomplete Patrol',
  stale_patrol: 'Stale Patrol',
  emergency_sos: 'SOS',
  vms_incident: 'Camera Incident',
};

// ============================================
// Helpers
// ============================================

/**
 * Find the highest-priority undismissed alert.
 * Filters out dismissed alerts, sorts by severity (critical first),
 * then by creation time (newest first), and returns the top result.
 */
function getTopAlert(alerts: DispatchAlert[]): DispatchAlert | null {
  const active = alerts.filter((a) => !a.dismissedAt);
  if (active.length === 0) return null;

  active.sort((a, b) => {
    const severityDiff =
      (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99);
    if (severityDiff !== 0) return severityDiff;

    // Same severity — newest first
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return active[0];
}

/**
 * Build a concise title string for the banner.
 * Format: "Alert Type — Guard Name"
 */
function buildTitle(alert: DispatchAlert): string {
  const typeLabel = ALERT_TYPE_LABELS[alert.type] || alert.type;
  return `${typeLabel} — ${alert.guardName}`;
}

// ============================================
// Component
// ============================================

const MobileAlertBanner: React.FC<MobileAlertBannerProps> = ({
  alerts,
  onView,
  collapsed = false,
}) => {
  const topAlert = getTopAlert(alerts);

  // No active alerts — render nothing
  if (!topAlert) {
    return null;
  }

  // Collapsed mode — subtle info bar
  if (collapsed) {
    return (
      <div className={styles.collapsedBar}>
        <span className={styles.collapsedBarIcon}>
          <InfoOutlinedIcon fontSize="inherit" />
        </span>
        <span className={styles.collapsedBarText}>
          Showing alerts inline below
        </span>
      </div>
    );
  }

  // Full banner
  const isCritical = topAlert.severity === 'critical';

  return (
    <div
      className={classNames(styles.banner, {
        [styles.critical]: isCritical,
        [styles.warning]: !isCritical,
      })}
      role="alert"
    >
      <span className={styles.dot} />
      <div className={styles.content}>
        <span className={styles.title}>{buildTitle(topAlert)}</span>
        <span className={styles.time}>
          {formatRelativeTime(topAlert.createdAt)}
        </span>
      </div>
      <button
        className={styles.viewButton}
        onClick={() => onView(topAlert)}
        aria-label={`View alert: ${buildTitle(topAlert)}`}
      >
        View
      </button>
    </div>
  );
};

export default MobileAlertBanner;
